// Contrat d’accès aux données. Une seule implémentation réelle (Supabase) ; une implémentation
// en mémoire sert aux tests et au développement, explicitement étiquetée, jamais en production.
import type { Booking, BookingSettings } from '../core/booking';
import type { Contact, Profile } from '../core/contact';

export interface Snapshot {
  profile: Profile | null;
  contacts: Contact[];
  /** Instant de la lecture réussie sur le serveur. */
  readAt: number;
  /** Réglages de la prise de rendez-vous (null : jamais enregistrés). */
  booking?: BookingSettings | null;
  /** Rendez-vous pris via la page publique. */
  bookings?: Booking[];
}

export type RepoEvent =
  | { type: 'contact'; contact: Contact }
  | { type: 'contact-removed'; id: string }
  | { type: 'profile'; profile: Profile }
  | { type: 'booking'; booking: Booking };

export type LiveStatus = 'connecting' | 'live' | 'down';

export interface Repo {
  /** Lecture complète, corbeille incluse. */
  loadAll(): Promise<Snapshot>;
  /** Crée ou met à jour ; renvoie la ligne telle que le serveur l’a enregistrée. */
  saveContact(c: Contact): Promise<Contact>;
  deleteContact(id: string, deletedAt: string): Promise<Contact>;
  restoreContact(id: string): Promise<Contact>;
  purgeContact(id: string): Promise<void>;
  saveProfile(p: Profile): Promise<Profile>;
  saveBookingSettings(s: BookingSettings): Promise<BookingSettings>;
  /** Annulation d’un rendez-vous par moi (le visiteur passe par son lien). */
  cancelBooking(id: string): Promise<Booking>;
  /** Modifications poussées par le serveur (autres appareils). Renvoie la fonction d’arrêt. */
  subscribe(onEvent: (e: RepoEvent) => void, onStatus: (s: LiveStatus) => void): () => void;
}

export class RepoError extends Error {
  constructor(message: string, public readonly offline = false, public override readonly cause?: unknown) {
    super(message);
    this.name = 'RepoError';
  }
}

/** Traduit une erreur quelconque en message lisible, en distinguant la panne réseau. */
export function describeError(e: unknown): RepoError {
  if (e instanceof RepoError) return e;
  const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  const network = /fetch|network|réseau|Failed to|NetworkError|ECONN|timeout/i.test(msg);
  if (network) return new RepoError('Le serveur n’a pas répondu (réseau).', true, e);
  const code = (e as { code?: string } | null)?.code;
  if (code === 'PGRST301' || code === '401' || /JWT|token/i.test(msg)) return new RepoError('Session expirée : reconnectez-vous.', false, e);
  if (code === '42501' || /row-level security|permission denied/i.test(msg)) return new RepoError('Accès refusé par les règles de sécurité.', false, e);
  if (/violates check constraint|invalid input/i.test(msg)) return new RepoError('Le serveur a refusé ces valeurs (' + msg + ').', false, e);
  return new RepoError(msg ? 'Erreur serveur : ' + msg : 'Erreur serveur inconnue.', false, e);
}
